import type { GetServerSideProps } from 'next'
import { useMemo } from 'react'
import PageContainer from 'shared-components/service/PageContainer'
import Layout from 'components/Layout'
import { getds } from 'ds'
import { publicConfig } from 'config'
import type { DSFacade } from 'types.sg.ds'
import ProductPageTemplate from 'templates/ProductPageTemplate'
import CategoryLandingTemplate from 'templates/CategoryLandingTemplate'
import CategoryBrowseTemplate from 'templates/CategoryBrowseTemplate'
import {
  fetchCategoryRail,
  fetchCategoryV3,
  fetchDiscussions,
  fetchRatingDistributions,
  fetchRelatedProducts,
  fetchSpecDistributions,
  resolveDealHuntPage,
  searchProducts,
} from 'lib/dealhunt/client'
import { fillMdaqs } from 'lib/dealhunt/mdaqsFallback'
import { productQuestions } from 'lib/community/discussions'
import type { ReaderQuestionsData } from 'components/product/ReaderQuestions'
import { CATEGORY_DEFAULT_SORT, DOMAIN_CURRENCY, MIN_MEASURED_PEERS } from 'lib/dealhunt/constants'
import {
  buildMetaTags,
  catalogDataLayer,
  categoryDescription,
  productDescription,
} from 'lib/dealhunt/head'
import { collectionGraph, productGraph } from 'lib/dealhunt/jsonLd'
import { buildSoundGuysReview } from 'lib/dealhunt/review'
import {
  categoryHitToCardModel,
  hitToCardModel,
  type ProductCardModel,
} from 'components/product/cardModel'
import {
  brandedBreadcrumbs,
  brandedTitle,
  dealHuntCategoryHref,
  dealHuntProductHref,
  productCategoryLeaf,
  productCategoryPath,
  productDisplayName,
  productSlug,
} from 'lib/dealhunt/url'
import { isDefaultView, parseFilters, type CategoryFilterState } from 'lib/dealhunt/filters'
import { loadLabCharts, type LabChartPaths } from 'lib/dealhunt/audioChart'
import {
  browseDataFromV3,
  deferDrawerModel,
  type CategoryBrowseData,
} from 'components/category/useCategoryBrowse'
import type {
  DealHuntPageData,
  RatingDistributionsResponse,
  SpecDistributionsResponse,
} from 'lib/dealhunt/types'

type CommonData = Awaited<ReturnType<DSFacade['getCommonData']>>['data']

/** "More <category>" rail post-filter shared by both sources: drop bare product
 *  (variant) records and the current product, then cap at 8. */
function railHits<T extends { fullSlug: string | null; recordType: string }>(
  hits: T[],
  currentLeaf: string | undefined
): T[] {
  return hits
    .filter(h => h.fullSlug && h.recordType !== 'product')
    .filter(h => h.fullSlug?.split('/').pop() !== currentLeaf)
    .slice(0, 8)
}

/** Category browse payload gSSP resolves for a branch node (model-driven rail,
 *  `/api/category/v3`) — the only category UI since the legacy sidebar was removed. */
interface CategoryBrowseProps {
  filters: CategoryFilterState
  browse: CategoryBrowseData
}

interface ProductSlugPageProps {
  common: CommonData
  ts: number
  preview: null
  pageData: DealHuntPageData
  pagePath: string
  alternatives: ProductCardModel[]
  specDistributions: SpecDistributionsResponse | null
  /** Featured-score class distributions, or `null` (card degrades to rings only). */
  ratingDistributions: RatingDistributionsResponse | null
  /** Real lab-measurement curves (measurement app, via `loadLabCharts`), or `null` when the
   *  product has none. */
  labCharts: LabChartPaths | null
  category: CategoryBrowseProps | null
  /** "Questions from readers" on a product (community discussions); null = no module. */
  readerQuestions: ReaderQuestionsData | null
}

const ProductSlugPage: React.VFC<ProductSlugPageProps> = ({
  common,
  ts,
  preview,
  pageData,
  pagePath,
  alternatives,
  specDistributions,
  ratingDistributions,
  labCharts,
  category,
  readerQuestions,
}) => {
  // `brandedTitle`, not a bare `${brand} ${name}` concat — node names often already carry
  // the brand ("AGPTEK M3"), and doubling it ("AGPTEK AGPTEK M3") leaked into titles and
  // JSON-LD (SEO audit H6).
  const productFullName =
    pageData.kind === 'product'
      ? brandedTitle(pageData.brandNode?.name, pageData.node.name)
      : pageData.node.name

  // Hoisted above the head build: the title fallback needs to know whether a SoundGuys
  // review actually exists (audit H6: 72% of PDPs promised a "review" we never wrote).
  // Built once per render and handed to ProductPageTemplate, which renders from it.
  const review = pageData.kind === 'product' ? buildSoundGuysReview(pageData.enrichment) : null

  // Only the FALLBACK templates get the site suffix — `seoTitle` is DealHunt-authored
  // and may already carry one, which we can't see from here.
  const metaTitle =
    pageData.kind === 'product'
      ? pageData.node.seoTitle ||
        `${productFullName} ${
          review && !review.promoted ? 'review, price & specs' : 'price & specs'
        } - SoundGuys`
      : pageData.node.seoTitle || `${pageData.node.name} - SoundGuys`

  const trailingSlash = publicConfig('useTrailingSlash')
  const appUrl = publicConfig('appUrl') ?? ''

  // Memoized on the per-navigation props: a fresh object per page (so the GTM `page-view`
  // push re-fires on client-side navigation) but stable across re-renders (so it doesn't
  // re-fire without one). See `catalogDataLayer` for the full contract.
  const gtmDataLayer = useMemo(
    () =>
      catalogDataLayer({
        pageType: pageData.kind === 'product' ? 'product_page' : 'category_page',
        title: metaTitle,
        url: pagePath,
        // The leaf category name — the same `category` value the interaction events carry
        // (the full trail is the events' `category_path`); for a category page the node
        // itself IS the category.
        category:
          pageData.kind === 'product' ? productCategoryLeaf(pageData.node) : pageData.node.name,
        brand: pageData.kind === 'product' ? pageData.brandNode?.name : null,
      }),
    [pageData, metaTitle, pagePath]
  )

  let metaTags
  let jsonLd: Record<string, unknown>[]

  if (pageData.kind === 'product') {
    const descriptionInput = {
      seoDescription: pageData.node.seoDescription,
      tagline: pageData.tagline,
      fullName: productFullName,
      score: review?.score ?? null,
    }
    // Two lines on purpose: the SERP snippet says how many owners wrote in, the JSON-LD
    // `description` below does not — no owner data of any kind goes into structured data
    // (owner-notes contract #13).
    const description = productDescription(descriptionInput)
    metaTags = buildMetaTags({
      title: metaTitle,
      description: productDescription({
        ...descriptionInput,
        ownerCount: pageData.enrichment.owners?.published_count ?? null,
      }),
      imageUrl: pageData.heroImage ?? pageData.node.imageUrl ?? null,
      ogType: 'product',
    })
    const priceCents =
      pageData.primaryProduct?.currentPriceCents ?? pageData.aggregates.bestPriceCents ?? null
    jsonLd = [
      productGraph({
        pageUrl: pagePath,
        appUrl,
        trailingSlash,
        title: metaTitle,
        description,
        // The H1 expression, not `productFullName` — displayName wins when DealHunt
        // supplies it, and brandedTitle skips a duplicated brand prefix.
        productName: productDisplayName(pageData),
        brandName: pageData.brandNode?.name ?? null,
        images: [
          pageData.heroImage,
          pageData.node.imageUrl,
          ...pageData.enrichment.reviewImages.map(i => i.url),
        ],
        mpn: pageData.node.modelNumber,
        category: productCategoryPath(pageData.node),
        // EVERY spec, not just the featured chips — the full table is now SSR'd too
        // (SEO audit C1: `additionalProperty` carried 3 of N specs, starving the
        // Product markup of exactly the data crawlers came for).
        specs: pageData.enrichment.specs.map(sp => ({
          name: sp.displayName ?? sp.specKey,
          value: sp.specValue,
          unit: sp.specUnit,
        })),
        // Same branded trail ProductPageTemplate renders, so markup and page agree.
        breadcrumbs: brandedBreadcrumbs(pageData),
        // A sponsored post is not a Review for structured data, whatever it scores.
        review:
          review && !review.promoted
            ? {
                score: review.score,
                publishedAt: review.publishedAt,
                summary: review.summary,
                reviewers: review.reviewers.map(r => ({ name: r.name, profileUrl: r.profileUrl })),
                pros: review.pros,
                cons: review.cons,
              }
            : null,
        priceCents,
        currency: DOMAIN_CURRENCY[pageData.primaryProduct?.domain ?? 'US'] ?? 'USD',
        // ASIN as `sku` (Google's identifier slot when no GTIN exists — audit H9); the
        // Offer's validity window is DealHunt-computed (Keepa cadence), omitted until shipped.
        sku: pageData.primaryProduct?.asin ?? null,
        priceValidUntil: pageData.primaryProduct?.priceValidUntil ?? null,
        // MSRP branch: the page itself says "suggested retail price at launch", so an
        // Offer here would contradict the visible copy.
        isMsrpFallback: priceCents === null && Boolean(pageData.priceFallback),
      }),
    ]
  } else {
    const description = categoryDescription({
      seoDescription: pageData.node.seoDescription,
      name: pageData.node.name,
    })
    metaTags = buildMetaTags({
      title: metaTitle,
      description,
      imageUrl: pageData.node.imageUrl ?? null,
    })
    // ItemList only for the DEFAULT view. A filtered or re-sorted grid canonicalises to
    // the unfiltered page, so listing its items would describe a different page than the
    // canonical claims.
    const listable =
      category && isDefaultView(category.filters, CATEGORY_DEFAULT_SORT) ? category.browse.hits : []
    jsonLd = [
      collectionGraph({
        pageUrl: pagePath,
        appUrl,
        trailingSlash,
        title: metaTitle,
        description,
        breadcrumbs: pageData.breadcrumbs,
        items: listable
          .filter(h => h.fullSlug)
          .map(h => ({
            name: h.displayName ?? brandedTitle(h.brand, h.canonicalName || h.title),
            url: `${appUrl}${dealHuntProductHref(
              h.fullSlug as string,
              h.brand,
              trailingSlash,
              h.brandSlug
            )}`,
          })),
      }),
    ]
  }

  const head = {
    metaTitle,
    canonicalLink: pagePath,
    metaTags,
    jsonLd,
  } as const

  // Key by fullSlug so client-side navigation between categories/products
  // remounts the body — otherwise hooks like useCategoryBrowse keep the
  // previous page's seeded state and the grid goes stale.
  const renderBody = (): JSX.Element => {
    if (pageData.kind === 'product') {
      return (
        <ProductPageTemplate
          key={pageData.node.fullSlug}
          data={pageData}
          alternatives={alternatives}
          specDistributions={specDistributions}
          ratingDistributions={ratingDistributions}
          labCharts={labCharts}
          review={review}
          readerQuestions={readerQuestions}
        />
      )
    }
    if (category) {
      return (
        <CategoryBrowseTemplate
          key={pageData.node.fullSlug}
          data={pageData}
          initialFilters={category.filters}
          initialBrowse={category.browse}
        />
      )
    }
    return <CategoryLandingTemplate key={pageData.node.fullSlug} data={pageData} />
  }

  return (
    <PageContainer
      layoutProps={{
        hideDealBar: true,
      }}
      layout={Layout}
      props={{
        preview,
        page: {
          sections: null,
          ampPagePath: null,
          head,
          pagePath,
          meta: {
            engagementContainerId: null,
            disabledAds: false,
            topics: [],
            dataLayer: gtmDataLayer,
            subscribeTags: ['Deals'],
            tags: [],
            categories: [],
            postId: null,
            sponsor: null,
            sponsored: false,
          },
        },
        common,
        ts,
        rss: false,
      }}
    >
      {renderBody()}
    </PageContainer>
  )
}

export const getServerSideProps: GetServerSideProps<ProductSlugPageProps> = async context => {
  const raw = context.params?.slug
  let segments: string[] = []
  if (Array.isArray(raw)) {
    segments = raw
  } else if (raw) {
    segments = [raw]
  }

  const dataSource = await getds()
  // `resolveDealHuntPage` returns null ONLY for a genuine miss (upstream 404) and throws
  // `UpstreamError` on a transient failure. Let that throw propagate → Next serves a 500
  // (retryable, no deindex). Do NOT catch it into `notFound` below — that would turn a
  // momentary API blip back into a hard 404 for a product that really exists.
  const [{ data: common }, pageData] = await Promise.all([
    dataSource.getCommonData(),
    resolveDealHuntPage(segments).then(page => page && fillMdaqs(page)),
  ])

  if (!pageData) {
    return { notFound: true }
  }

  // Same edge-cache contract as the coverage pages (editorial/page.tsx): these pages are
  // public listings/PDPs, identical for every visitor — the body varies by route + the
  // whitelisted query params only, and gSSP reads no cookies or auth. 15-min edge TTL is
  // well inside the "price last checked Nh ago" freshness the page itself claims (SEO
  // audit H8: `no-store` on 2,400+ pages meant zero edge caching and multi-second TTFBs).
  context.res.setHeader('Cache-Control', 'public, s-maxage=900, stale-while-revalidate=86400')

  const trailingSlash = publicConfig('useTrailingSlash')
  const appUrl = publicConfig('appUrl') ?? ''

  let alternatives: ProductCardModel[] = []
  let specDistributions: SpecDistributionsResponse | null = null
  let ratingDistributions: RatingDistributionsResponse | null = null
  let labCharts: LabChartPaths | null = null
  let category: CategoryBrowseProps | null = null
  let readerQuestions: ReaderQuestionsData | null = null
  let pagePath: string

  if (pageData.kind === 'product') {
    const brand = pageData.brandNode?.name ?? null
    // Prefer DealHunt's canonical brand slug over slugifying the display name, so the brand
    // prefix matches the taxonomy leaf exactly (no lossy "Bang & Olufsen" → "bang-olufsen").
    const brandSlug = pageData.brandNode?.slug ?? null
    // Canonicalize to the brand-prefixed slug: 301 any legacy brand-less or otherwise
    // non-canonical product URL so there's a single indexable URL per product.
    const canonicalSlug = productSlug(brand, pageData.node.fullSlug, brandSlug)
    if (segments.length === 1 && segments[0] !== canonicalSlug) {
      return {
        redirect: {
          destination: dealHuntProductHref(pageData.node.fullSlug, brand, trailingSlash, brandSlug),
          statusCode: 301,
        },
      }
    }
    pagePath = appUrl + dealHuntProductHref(pageData.node.fullSlug, brand, trailingSlash, brandSlug)
    const currentLeaf = pageData.node.fullSlug.split('/').pop()
    // "More <category>" rail. Prefer /api/category/v2 scoped to the product's category
    // node: it sorts server-side and carries sgReviewScore + displayName,
    // so the cards get the /10 chip + canonical title for free (migration doc A15/B1).
    // Scope: relatedSubcategory (tight/curated — same peers as the lab comparisons) is
    // preferred, then relatedNodeId (broad). Both are served (A15); when neither resolves
    // for a line, fall back to the direct-Meili popular rail, and finally a legacy name
    // search (neither carries the SG score, so those fallback cards show no chip).
    const railNodeId = pageData.node.relatedSubcategory?.id ?? pageData.node.relatedNodeId ?? null
    const relatedPath = productCategoryPath(pageData.node)
    const nameQuery = pageData.node.relatedNodeName ?? pageData.node.name
    // The rail sorts by `sg_score` (RAIL_SORT): reviewed products first, score
    // descending, unscored tail last — a recommendation strip leads with gear we
    // actually reviewed (supersedes the earlier recency-first `recent_popular`
    // pin). Deliberately its own constant even though category pages now default to
    // `sg_score` too (CATEGORY_DEFAULT_SORT, 2026-08-11) — the rail and listing sorts
    // are separate editorial choices. The fallbacks below can't honor it
    // (Meili/name-search) and stay popularity-ordered.
    const railCards: Promise<ProductCardModel[]> = railNodeId
      ? fetchCategoryRail(railNodeId)
          .then(listing =>
            railHits(listing?.hits ?? [], currentLeaf).map(h => categoryHitToCardModel(h))
          )
          .catch(() => [])
      : fetchRelatedProducts(relatedPath)
          .then(
            async hits =>
              hits ??
              (await searchProducts({ q: nameQuery, hitsPerPage: 14, distinct: true }))?.hits ??
              []
          )
          .then(hits => railHits(hits, currentLeaf).map(hitToCardModel))
          .catch(() => [])
    // Pin the distributions to the featured spec keys (the chips) for a 1:1 match.
    const featuredKeys = pageData.enrichment.specs.filter(s => s.isFeatured).map(s => s.specKey)
    // `?chartId=` overrides the resolved ClearBuy id for QA (e.g. before DealHunt populates it).
    const chartOverride = Array.isArray(context.query.chartId)
      ? context.query.chartId[0]
      : context.query.chartId
    const chartId =
      chartOverride && Number.isFinite(Number(chartOverride))
        ? Number(chartOverride)
        : pageData.clearbuyProductId
    const [cards, distributions, scoreDistributions, charts, questions] = await Promise.all([
      railCards,
      fetchSpecDistributions(pageData.node.fullSlug, featuredKeys).catch(() => null),
      // Optional enrichment (roll-forward contract): null until DealHunt ships the
      // endpoint — the scores card then falls back to our own defaults, rings only.
      // No `keys` pin: WHICH scores are featured is now a per-category editorial
      // choice made in SG Studio, and the card renders whatever comes back. The peer
      // gate rides along so a thin score widens to a broader class instead of
      // arriving under our chart threshold.
      fetchRatingDistributions(pageData.node.fullSlug, undefined, MIN_MEASURED_PEERS).catch(
        () => null
      ),
      loadLabCharts(chartId).catch(() => null),
      // Optional (community discussions): a DealHunt without /discussions, a failure or a list
      // DealHunt did not filter to this product leaves the module out (`productQuestions`).
      fetchDiscussions({ product: pageData.node.id, limit: 3 }).catch(() => null),
    ])
    specDistributions = distributions
    ratingDistributions = scoreDistributions
    labCharts = charts
    // Always the module (its ask is the first question's door); "nobody has asked" only when
    // DealHunt answered THIS product's list (`read`), never on a failed read.
    readerQuestions = {
      items: productQuestions(questions, pageData.node.id),
      read: questions?.product === pageData.node.id && Array.isArray(questions?.items),
      productSlug: canonicalSlug,
      now: Date.now(),
    }
    alternatives = cards
  } else {
    pagePath = appUrl + dealHuntCategoryHref(pageData.node.fullSlug, trailingSlash)
    // Listings lead with the SoundGuys score (CATEGORY_DEFAULT_SORT) — see constants.ts.
    const filters = parseFilters(context.query, CATEGORY_DEFAULT_SORT)
    // Model-driven rail: one v3 round-trip (listing + render-ready filter model).
    // No failover tier: same origin — a miss renders the landing template instead.
    const v3 = await fetchCategoryV3(pageData.node.id, filters).catch(() => null)
    if (v3) {
      // The closed drawer's model rides client-side on first open (deferDrawerModel).
      category = { filters, browse: deferDrawerModel(browseDataFromV3(v3)) }
    }
  }

  return {
    props: {
      common,
      ts: new Date().getTime(),
      preview: null,
      pageData,
      pagePath,
      alternatives,
      specDistributions,
      ratingDistributions,
      labCharts,
      category,
      readerQuestions,
    },
  }
}

export default ProductSlugPage
